import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl-next';

import { Manager, Reference } from '@atlaskit/popper';
import { token } from '@atlaskit/tokens';
import Portal from '@atlaskit/portal';
import { Box, Text, Stack } from '@atlaskit/primitives/compiled';
import { cssMap } from '@atlaskit/css';

import { useChoreographerAssets } from '@confluence/choreographer-services';
import { usePreloadImages } from '@confluence/content-types-utils/entry-points/usePreloadImages';
import { expValEquals } from '@confluence/feature-experiments';
import { isLocaleEnglish } from '@confluence/onboarding-helpers/entry-points/isLocaleEnglish';
import { useSessionData } from '@confluence/session-data';
import { fg } from '@confluence/feature-gating';

import { CONTENT_TYPE_HOVER_CARD_CONTENT_MAPPING } from './contextualCreateHoverCardContentMapping';
import type { ContentType } from './contextualCreateHoverCardContentMapping';

const styles = cssMap({
	hoverContainerStyles: {
		width: '270px',
		boxShadow: token('elevation.shadow.raised'),
		borderRadius: token('radius.small'),
		overflow: 'hidden',
	},
	spotlightContainerStyles: {
		padding: token('space.200'),
		backgroundColor: token('color.background.discovery.bold'),
		color: token('color.text.inverse'),
		paddingInline: token('space.250'),
		paddingBlock: token('space.200'),
	},
	imageContainerStyles: {
		width: '100%',
		height: '160px',
		boxSizing: 'border-box',
	},
	imageStyles: {
		width: '100%',
		height: '100%',
	},
});

type ContextualCreateHoverCardType = {
	children?: React.ReactNode;
	contentType: ContentType;
};

const ContextualCreateHoverCardComponent = ({
	children,
	contentType,
}: ContextualCreateHoverCardType) => {
	const [showPopup, setShowPopup] = useState(false);
	const { Popper, useChoreographer } = useChoreographerAssets();
	const { text, image } = CONTENT_TYPE_HOVER_CARD_CONTENT_MAPPING[contentType];
	const { locale } = useSessionData();
	usePreloadImages([image]);

	const hoverItemChoreographer = useChoreographer('ContextualCreateHoverItem', {
		start: () => setShowPopup(true),
		stop: () => setShowPopup(false),
	});

	let timer: ReturnType<typeof setTimeout>;
	const handleMouseEnter = () => {
		// Product requirement to prevent the gifs from showing immediately
		timer = setTimeout(() => {
			void hoverItemChoreographer.startMessage();
		}, 50);
	};

	const handleMouseLeave = () => {
		void hoverItemChoreographer.stopMessage();
		clearTimeout(timer);
	};

	return (
		<Manager>
			<Reference>
				{({ ref }) => (
					<Box ref={ref} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
						{children}
					</Box>
				)}
			</Reference>
			{showPopup &&
				isLocaleEnglish(locale) &&
				expValEquals(
					'cc_feature_disc_content_type_visuals_in_create_new',
					'cohort',
					'experiment',
				) && (
					<Popper messageType="transactional" placement="right-start" offset={[0, 12]}>
						{({ ref, style }) => (
							<Portal zIndex={800}>
								{/* eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766 */}
								<div ref={ref} style={style} css={styles.hoverContainerStyles}>
									<div css={styles.imageContainerStyles}>
										<img css={styles.imageStyles} src={image} alt="" />
									</div>
									<Stack xcss={styles.spotlightContainerStyles} space="space.100">
										<Text color="color.text.inverse">
											<FormattedMessage {...text} />
										</Text>
									</Stack>
								</div>
							</Portal>
						)}
					</Popper>
				)}
		</Manager>
	);
};

export const ContextualCreateHoverCard = ({
	children,
	...props
}: ContextualCreateHoverCardType) => {
	return fg('cc_feature_disc_content_type_visuals_create_gate') ? (
		<ContextualCreateHoverCardComponent {...props}>{children}</ContextualCreateHoverCardComponent>
	) : (
		children
	);
};
